const STORAGE_KEY = "bs_device_id";
const HASH_KEY    = "bs_device_hash";
const SALT_KEY    = "bs_device_salt";

/** Generate SHA-256 hex dari string menggunakan Web Crypto API */
async function sha256(str) {
  const buf    = new TextEncoder().encode(str);
  const digest = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/** Ambil atau buat salt unik per browser instance */
function getOrCreateSalt() {
  let salt = localStorage.getItem(SALT_KEY);
  if (!salt) {
    salt = crypto.randomUUID();
    localStorage.setItem(SALT_KEY, salt);
  }
  return salt;
}

/** Kumpulkan sinyal browser + salt unik per instance */
function collectFingerprint() {
  const nav = window.navigator;
  const scr = window.screen;
  const salt = getOrCreateSalt();
  const signals = [
    salt,
    nav.userAgent        || "",
    nav.language         || "",
    nav.hardwareConcurrency ?? "",
    nav.platform         || "",
    scr.width            ?? "",
    scr.height           ?? "",
    scr.colorDepth       ?? "",
    scr.pixelDepth       ?? "",
    Intl.DateTimeFormat().resolvedOptions().timeZone || "",
    new Date().getTimezoneOffset(),
  ];
  return signals.join("|");
}

/**
 * Inisialisasi device ID:
 * - Jika sudah ada di localStorage, kembalikan langsung.
 * - Jika belum, generate hash → POST ke /api/device-id → simpan UUID.
 * Mengembalikan { deviceId, hash } atau null jika gagal.
 */
export async function initDeviceId() {
  if (typeof window === "undefined") return null;

  try {
    const existing = localStorage.getItem(STORAGE_KEY);
    if (existing) return { deviceId: existing, hash: localStorage.getItem(HASH_KEY) };

    const fingerprint = collectFingerprint();
    const hash = await sha256(fingerprint);

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    let res;
    try {
      res = await fetch("/api/device-id", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ hash }),
        signal: controller.signal,
      });
    } finally {
      clearTimeout(timeout);
    }

    if (!res.ok) throw new Error(`device-id API error: ${res.status}`);
    const json = await res.json();
    const uuid = json?.data?.uuid;
    if (!uuid) throw new Error("No UUID in device-id response");

    localStorage.setItem(STORAGE_KEY, uuid);
    localStorage.setItem(HASH_KEY, hash);
    return { deviceId: uuid, hash };
  } catch (err) {
    console.warn("[deviceId] Init failed:", err);
    return null;
  }
}

/** Baca device ID yang sudah tersimpan (sync, no-fetch) */
export function getStoredDeviceId() {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(STORAGE_KEY) || null;
}
